import { useCallback, useEffect, useMemo } from 'react'
import uniqueId from 'lodash/uniqueId'
import { getDefaultDimensionAggregation, getTypeName } from '@rawgraphs/rawgraphs-core'
import { getChartRecommendations } from '../services/chartRecommendations'
import { aiChartAdvisor } from '../services/chartAgentService'

export default function useRecommendationMapping({
  auditSummary,
  userData,
  currentChart,
  data,
  activeRecommendation,
  setActiveRecommendation,
  handleChartChange,
  setActiveMapTab,
  setCurrentStep,
  setMapping,
  dataMappingRef,
  sampleChartTypes,
}) {
  const recommendations = useMemo(
    () => getChartRecommendations(auditSummary, sampleChartTypes),
    [auditSummary, sampleChartTypes]
  )

  const aiResult = useMemo(
    () =>
      aiChartAdvisor({
        auditSummary,
        schema: auditSummary?.schema,
        sampleRows: (userData || []).slice(0, 5),
        currentChart,
      }),
    [auditSummary, userData, currentChart]
  )

  const buildRecommendedMapping = useCallback(
    (recommendation) => {
      if (
        !recommendation?.suggestedMapping ||
        !data?.dataTypes ||
        !currentChart?.dimensions
      ) {
        return null
      }

      const nextMapping = {}
      currentChart.dimensions.forEach((dimension) => {
        const suggestedColumns = recommendation.suggestedMapping[dimension.id]
        if (!Array.isArray(suggestedColumns) || suggestedColumns.length === 0) {
          return
        }

        const validColumns = suggestedColumns.filter((column) =>
          Object.prototype.hasOwnProperty.call(data.dataTypes, column)
        )
        if (validColumns.length === 0) {
          return
        }

        const mappedTypes = validColumns.map((column) =>
          getTypeName(data.dataTypes[column])
        )
        const isValid =
          dimension.validTypes?.length === 0 ||
          mappedTypes.every((mappedType) =>
            dimension.validTypes?.includes(mappedType)
          )

        nextMapping[dimension.id] = {
          ids: validColumns.map(() => uniqueId()),
          value: validColumns,
          isValid,
          mappedType: mappedTypes[0],
          config: dimension.aggregation
            ? {
                aggregation: validColumns.map((column) =>
                  getDefaultDimensionAggregation(dimension, data.dataTypes[column])
                ),
              }
            : undefined,
        }
      })

      return Object.keys(nextMapping).length > 0 ? nextMapping : null
    },
    [currentChart, data]
  )

  const handleRecommendationSelect = useCallback(
    (chart, recommendation) => {
      setActiveRecommendation(recommendation || null)
      handleChartChange(chart)
      setActiveMapTab('mapping')
      setCurrentStep('map')
    },
    [
      handleChartChange,
      setActiveMapTab,
      setActiveRecommendation,
      setCurrentStep,
    ]
  )

  useEffect(() => {
    if (!activeRecommendation || !currentChart || !data) {
      return
    }

    if (activeRecommendation.chartKey !== currentChart.metadata.id) {
      return
    }

    const nextMapping = buildRecommendedMapping(activeRecommendation)
    if (nextMapping) {
      setMapping(nextMapping)
      if (dataMappingRef.current) {
        dataMappingRef.current.applyMapping(nextMapping)
      }
    }
  }, [
    activeRecommendation,
    buildRecommendedMapping,
    currentChart,
    data,
    dataMappingRef,
    setMapping,
  ])

  return {
    recommendations,
    aiResult,
    handleRecommendationSelect,
  }
}
